/**
 * IdArrayShrinker - Main class for shrinking/expanding ID arrays.
 */

import { strategies } from "./strategies";
import { ShrinkType, ShrunkIdArray } from "./types";

/** Object overhead + properties on a ShrunkIdArray. */
const BASE_OVERHEAD = 32;

/**
 * Estimates the memory size of a ShrunkIdArray in bytes.
 * @param shrunk - The shrunk ID array to measure
 * @returns Estimated memory footprint in bytes
 */
export function estimateShrunkSize(shrunk: ShrunkIdArray): number {
    if (shrunk.data instanceof Uint8Array || shrunk.data instanceof Uint32Array) {
        return BASE_OVERHEAD + shrunk.data.byteLength;
    }
    return BASE_OVERHEAD + shrunk.data.length * 8;
}

/**
 * Pick the smallest shrink strategy for `ids` in a single pass over the
 * strategy table. Callers that need both the mode and the size (e.g.
 * packDescendants, which would otherwise estimate and then re-detect inside
 * `shrink`) should use this and pass `type` into `IdArrayShrinker.shrink`.
 *
 * Allocation-free: each strategy's `estimateSize` is at worst a linear scan.
 *
 * @param ids - Distinct, sorted IDs (plain array or typed array)
 */
export function pickBestShrink(ids: readonly number[] | Uint32Array): { type: ShrinkType; size: number } {
    if (ids.length === 0) {
        return { type: ShrinkType.None, size: BASE_OVERHEAD };
    }

    const minId = ids[0];
    const maxId = ids[ids.length - 1];
    let bestType: ShrinkType = ShrinkType.None;
    let bestPayload = Infinity;
    for (const [type, strategy] of Object.entries(strategies)) {
        const payload = strategy.estimateSize(ids, minId, maxId);
        if (payload < bestPayload) {
            bestPayload = payload;
            bestType = type as ShrinkType;
        }
    }
    return { type: bestType, size: BASE_OVERHEAD + bestPayload };
}

/**
 * Estimate the bytes this id array would occupy if shrunk with the
 * best-fitting strategy, without allocating the shrunk form.
 *
 * @param ids - Array of distinct, sorted IDs
 */
export function estimateBestShrunkSize(ids: readonly number[] | Uint32Array): number {
    return pickBestShrink(ids).size;
}

export class IdArrayShrinker {
    /**
     * Shrink an array of IDs using the specified mode, or auto-detect the best mode.
     *
     * @param ids - Array of distinct, sorted IDs
     * @param mode - Optional shrink mode. If not provided, auto-detects the best mode.
     * @returns ShrunkIdArray that can be expanded back to the original
     */
    static shrink(ids: readonly number[] | Uint32Array, mode?: ShrinkType): ShrunkIdArray {
        if (ids.length === 0) {
            return { type: ShrinkType.None, data: [], length: 0 };
        }

        const selectedMode = mode ?? pickBestShrink(ids).type;
        return strategies[selectedMode].shrink(ids);
    }

    /**
     * Expand a ShrunkIdArray back to the original sorted unique ids.
     *
     * Returns a `Uint32Array` so callers that already operate on typed arrays
     * (SelectionSet, ObjectIdSet, descendants cache) can wrap it without an
     * intermediate `number[]` copy. The None strategy may return the stored
     * buffer as-is; do not mutate the result.
     */
    static expand(shrunk: ShrunkIdArray): Uint32Array {
        return strategies[shrunk.type].expand(shrunk);
    }

    /**
     * Auto-detect the best shrink mode based on data characteristics.
     * Estimates memory usage for each mode and picks the smallest.
     * @param ids - Array of distinct, sorted IDs
     * @returns The optimal ShrinkType for this data
     */
    static detectBestMode(ids: readonly number[] | Uint32Array): ShrinkType {
        return pickBestShrink(ids).type;
    }
}
